import { useCallback, useEffect, useRef, useState } from "react";
import { AdminShell } from "@/client/components/admin/AdminShell";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Notice } from "@/client/components/ui/notice";
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogPanel,
  DialogFooter,
} from "@/client/components/ui/dialog";
import {
  LockIcon,
  LockOpenIcon,
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  KeyRoundIcon,
  InfoIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  AlertCircleIcon,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  CheckIcon,
} from "lucide-react";
import {
  setupVault,
  unlockVault,
  encryptEntry,
  decryptEntry,
  type VaultCrypto,
  type EntryPlain,
} from "@/client/lib/kluis-crypto";

const BODY_MAX = 10_000;

type VaultMeta = VaultCrypto & { passwordHint: string | null };
type RawEntry = { id: number; iv: string; ciphertext: string };
type Entry = { id: number; title: string; body: string };
type Phase = "loading" | "setup" | "locked" | "unlocked";

const textareaClass =
  "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs outline-none ring-ring/24 transition-shadow placeholder:text-muted-foreground/72 focus-visible:border-ring focus-visible:ring-[3px]";

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button type="button" onClick={copy} className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
      {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
      {label && <span>{copied ? "Gekopieerd" : label}</span>}
    </button>
  );
}

async function apiJson(path: string, init?: RequestInit): Promise<{ ok: boolean; status: number; data: any }> {
  const res = await fetch(`/api/kluis${path}`, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* no body */
  }
  return { ok: res.ok, status: res.status, data };
}

// Compact info alert with a link to the full "how it works / how safe" explanation in a dialog.
// Shown on the setup + unlock screens.
function SecurityNote() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="rounded-md border px-4 py-3">
        <div className="flex gap-3">
          <InfoIcon aria-hidden className="mt-0.5 shrink-0 text-blue-500" size={16} />
          <div className="flex grow flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <p className="text-sm text-muted-foreground">
              Alles wordt in je eigen browser versleuteld; de server bewaart alleen versleutelde gegevens.
            </p>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="group shrink-0 cursor-pointer whitespace-nowrap text-sm font-medium text-primary"
            >
              Hoe werkt dit?
              <ArrowRightIcon aria-hidden className="-mt-0.5 ms-1 inline-flex opacity-60 transition-transform group-hover:translate-x-0.5" size={16} />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPopup className="w-[94vw] max-w-lg">
          <DialogHeader>
            <DialogTitle>Hoe veilig is de Kluis?</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            <div className="space-y-3 text-sm leading-6 text-muted-foreground">
              <p>
                <span className="font-medium text-foreground">Versleuteld in je browser.</span> Je kiest
                een Kluis-wachtwoord. Daaruit maakt je browser een sleutel waarmee je titel en tekst
                lokaal worden versleuteld (AES-256) voordat er iets naar KUVO wordt verstuurd.
              </p>
              <p>
                <span className="font-medium text-foreground">De server ziet niets leesbaars.</span> KUVO
                bewaart alleen de versleutelde gegevens, wat technische instellingen en je (onversleutelde)
                hint. Je wachtwoord wordt nooit verstuurd of opgeslagen; we controleren in je browser of het
                je Kluis kan openen.
              </p>
              <p>
                <span className="font-medium text-foreground">Niet herstelbaar.</span> Omdat alleen jij het
                wachtwoord hebt, kan KUVO je Kluis niet openen of herstellen. Raak je het wachtwoord kwijt,
                dan zijn je items niet meer te ontsleutelen. Kies dus iets sterks dat je niet vergeet,
                bijvoorbeeld een wachtwoordzin van een paar woorden.
              </p>
              <p>
                <span className="font-medium text-foreground">Wat het wel en niet beschermt.</span> Zolang je
                een sterk wachtwoord gebruikt, blijft je inhoud beschermd als de database ooit zou lekken. Het
                beschermt niet tegen malware of schadelijke browser-extensies op je eigen apparaat, en ook niet
                als iemand meekijkt terwijl je Kluis open staat. Sluit 'm na gebruik.
              </p>
            </div>
          </DialogPanel>
          <DialogFooter>
            <Button onClick={() => setOpen(false)}>Begrepen</Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
}

// ── Setup: choose a Kluis password (first time) ──
function SetupCard({ onCreated }: { onCreated: (key: CryptoKey, meta: VaultMeta) => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 10 && confirm === password && !busy;

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const { crypto, vaultKey } = await setupVault(password);
      const res = await apiJson("/vault", {
        method: "POST",
        body: JSON.stringify({ ...crypto, passwordHint: hint.trim() || null }),
      });
      if (!res.ok) {
        setError("Het aanmaken van de Kluis is niet gelukt. Probeer het opnieuw.");
        setBusy(false);
        return;
      }
      onCreated(vaultKey, { ...crypto, passwordHint: hint.trim() || null });
    } catch {
      setError("Er ging iets mis bij het versleutelen. Probeer het opnieuw.");
      setBusy(false);
    }
  }

  return (
    <div className="max-w-lg space-y-5">
      <SecurityNote />
      <form onSubmit={handleCreate} className="space-y-4 rounded-lg border p-5">
        <div>
          <h2 className="text-base font-semibold">Kies een Kluis-wachtwoord</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Dit wachtwoord verlaat je browser nooit en kan niet door KUVO worden hersteld. Kies iets
            sterks, bijvoorbeeld een wachtwoordzin van een paar woorden. Raak je het kwijt, dan zijn je
            Kluis-items niet meer te ontsleutelen.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="kluis-pw">Wachtwoord</Label>
          <Input id="kluis-pw" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={tooShort} />
          {tooShort && <p className="text-xs text-destructive">Gebruik minstens 10 tekens.</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="kluis-pw2">Herhaal wachtwoord</Label>
          <Input id="kluis-pw2" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={mismatch} />
          {mismatch && <p className="text-xs text-destructive">De wachtwoorden komen niet overeen.</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="kluis-hint">Hint (optioneel)</Label>
          <Input id="kluis-hint" value={hint} onChange={(e) => setHint(e.target.value)} maxLength={200} placeholder="bijv. In mijn wachtwoordmanager onder KUVO" />
          <p className="text-xs text-amber-700">
            Let op: de hint wordt niet versleuteld. Zet hier nooit je wachtwoord of een deel ervan in.
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button type="submit" disabled={!canSubmit}>
          {busy ? "Bezig..." : "Kluis aanmaken"}
        </Button>
      </form>
    </div>
  );
}

// ── Unlock: enter the Kluis password ──
function UnlockCard({ meta, onUnlocked }: { meta: VaultMeta; onUnlocked: (key: CryptoKey) => void }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState(false);

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setWrong(false);
    try {
      const key = await unlockVault(password, meta);
      if (!key) {
        setWrong(true);
        setBusy(false);
        return;
      }
      onUnlocked(key);
    } catch {
      setWrong(true);
      setBusy(false);
    }
  }

  return (
    <div className="max-w-lg space-y-5">
      <SecurityNote />
      <form onSubmit={handleUnlock} className="space-y-4 rounded-lg border p-5">
        <div className="flex items-center gap-2">
          <LockIcon className="size-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Open je Kluis</h2>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="kluis-unlock">Kluis-wachtwoord</Label>
          <Input id="kluis-unlock" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={wrong} autoFocus />
        </div>

        {wrong && (
          <div className="rounded-md border px-4 py-3 text-sm">
            <p className="font-medium text-foreground">
              <AlertTriangleIcon aria-hidden className="-mt-0.5 me-2 inline-flex text-amber-500" size={16} />
              Dit lijkt niet je gebruikelijke Kluis-wachtwoord
            </p>
            <p className="mt-1.5 text-muted-foreground">
              Je wachtwoord wordt niet opgeslagen; de browser controleert lokaal of dit wachtwoord je Kluis kan openen. Probeer een ander wachtwoord{meta.passwordHint ? " of gebruik je hint" : ""}.
            </p>
            {meta.passwordHint && <p className="mt-2 text-muted-foreground">Hint: {meta.passwordHint}</p>}
          </div>
        )}

        <Button type="submit" disabled={!password || busy}>
          {busy ? "Openen..." : "Openen"}
        </Button>
      </form>
    </div>
  );
}

// ── Entry editor dialog (add / edit) ──
function EntryDialog({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: Entry | null;
  onClose: () => void;
  onSave: (title: string, body: string) => Promise<boolean>;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? "");
      setBody(initial?.body ?? "");
      setError(false);
      setBusy(false);
    }
  }, [open, initial]);

  async function handleSave() {
    if (!title.trim() || busy) return;
    setBusy(true);
    setError(false);
    const ok = await onSave(title.trim(), body);
    if (!ok) {
      setError(true);
      setBusy(false);
      return;
    }
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPopup className="w-[94vw] max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial ? "Item bewerken" : "Nieuw item"}</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="entry-title">Titel</Label>
              <Input id="entry-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entry-body">Inhoud</Label>
              <textarea
                id="entry-body"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
                rows={10}
                maxLength={BODY_MAX}
                className={textareaClass}
              />
              <p className="text-right text-xs text-muted-foreground tabular-nums">
                {body.length} / {BODY_MAX}
              </p>
            </div>
            {error && <p className="text-sm text-destructive">Opslaan is niet gelukt. Probeer het opnieuw.</p>}
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={handleSave} disabled={!title.trim() || busy}>{busy ? "Opslaan..." : "Opslaan"}</Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

// ── View entry dialog (read-only with blur + copy) ──
function ViewEntryDialog({
  open,
  entry,
  defaultBlurContent,
  onClose,
}: {
  open: boolean;
  entry: Entry | null;
  defaultBlurContent: boolean;
  onClose: () => void;
}) {
  const [blurred, setBlurred] = useState(defaultBlurContent);

  useEffect(() => {
    if (open) setBlurred(defaultBlurContent);
  }, [open, defaultBlurContent]);

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPopup className="w-[94vw] max-w-lg">
        <DialogHeader>
          <DialogTitle>Item bekijken</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Titel</p>
                <CopyButton text={entry.title} label="Kopiëren" />
              </div>
              <p className="mt-1 text-sm font-medium text-foreground">{entry.title}</p>
            </div>
            {entry.body && (
              <div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Inhoud</p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setBlurred(!blurred)}
                      className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {blurred ? <EyeIcon size={12} /> : <EyeOffIcon size={12} />}
                      <span>{blurred ? "Tonen" : "Verbergen"}</span>
                    </button>
                    <CopyButton text={entry.body} label="Kopiëren" />
                  </div>
                </div>
                <p className={`mt-1 whitespace-pre-wrap text-sm text-foreground transition-all ${blurred ? "select-none blur-sm" : ""}`}>
                  {entry.body}
                </p>
              </div>
            )}
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Sluiten</Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

// ── Unlocked vault: list + add/edit/delete ──
function VaultView({ vaultKey, onLock }: { vaultKey: CryptoKey; onLock: () => void }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Entry | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [viewing, setViewing] = useState<Entry | null>(null);
  const [blurTitles, setBlurTitles] = useState(false);
  const [blurContent, setBlurContent] = useState(true);

  const load = useCallback(async () => {
    setLoadError(false);
    const res = await apiJson("/entries");
    if (!res.ok) {
      setLoadError(true);
      setLoading(false);
      return;
    }
    const raw: RawEntry[] = res.data.entries ?? [];
    const decrypted: Entry[] = [];
    for (const e of raw) {
      try {
        const plain = await decryptEntry(vaultKey, e.iv, e.ciphertext);
        decrypted.push({ id: e.id, ...plain });
      } catch {
        decrypted.push({ id: e.id, title: "(kan niet ontsleutelen)", body: "" });
      }
    }
    setEntries(decrypted);
    setLoading(false);
  }, [vaultKey]);

  useEffect(() => { load(); }, [load]);

  const saveEntry = useCallback(
    async (title: string, body: string): Promise<boolean> => {
      try {
        const { iv, ciphertext } = await encryptEntry(vaultKey, { title, body } as EntryPlain);
        if (editing) {
          const res = await apiJson(`/entries/${editing.id}`, { method: "PUT", body: JSON.stringify({ iv, ciphertext }) });
          if (!res.ok) return false;
        } else {
          const res = await apiJson("/entries", { method: "POST", body: JSON.stringify({ iv, ciphertext }) });
          if (!res.ok) return false;
        }
        await load();
        return true;
      } catch {
        return false;
      }
    },
    [vaultKey, editing, load],
  );

  async function handleDelete(id: number) {
    setDeletingId(id);
    const res = await apiJson(`/entries/${id}`, { method: "DELETE" });
    if (res.ok) setEntries((prev) => prev.filter((e) => e.id !== id));
    setDeletingId(null);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <LockOpenIcon className="size-4 text-emerald-600" />
          Kluis geopend. Sluit 'm na gebruik; je items zijn alleen nu ontsleuteld in deze tab.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setBlurTitles(!blurTitles)}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
              blurTitles ? "border-primary/30 bg-primary/10 text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {blurTitles ? <EyeOffIcon size={13} /> : <EyeIcon size={13} />}
            Blur titels
          </button>
          <button
            type="button"
            onClick={() => setBlurContent(!blurContent)}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
              blurContent ? "border-primary/30 bg-primary/10 text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {blurContent ? <EyeOffIcon size={13} /> : <EyeIcon size={13} />}
            Blur inhoud
          </button>
          <Button size="sm" variant="outline" onClick={onLock}>
            <LockIcon /> Vergrendelen
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setEditorOpen(true); }}>
            <PlusIcon /> Nieuw item
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 w-full rounded-lg bg-muted" />)}
        </div>
      ) : loadError ? (
        <Notice tone="error" icon={<AlertCircleIcon />} title="Kan de items niet laden" action={<Button size="sm" variant="outline" onClick={() => { setLoading(true); load(); }}>Opnieuw proberen</Button>}>
          Probeer het opnieuw.
        </Notice>
      ) : entries.length === 0 ? (
        <div className="rounded-lg border bg-muted/20 px-6 py-10 text-center">
          <p className="text-sm text-foreground">Nog geen items in je Kluis.</p>
          <p className="mt-1 text-sm text-muted-foreground">Voeg een item toe; het wordt versleuteld opgeslagen.</p>
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border">
          {entries.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <p className={`min-w-0 truncate text-sm font-medium text-foreground transition-all ${blurTitles ? "select-none blur-sm" : ""}`}>
                {e.title || "(zonder titel)"}
              </p>
              <div className="flex shrink-0 items-center gap-1">
                <button type="button" onClick={() => setViewing(e)} aria-label="Bekijken" className="flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                  <EyeIcon className="size-4" />
                </button>
                <button type="button" onClick={() => { setEditing(e); setEditorOpen(true); }} aria-label="Bewerken" className="flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                  <PencilIcon className="size-4" />
                </button>
                <button type="button" onClick={() => handleDelete(e.id)} disabled={deletingId === e.id} aria-label="Verwijderen" className="flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-destructive disabled:opacity-50">
                  <Trash2Icon className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <EntryDialog open={editorOpen} initial={editing} onClose={() => setEditorOpen(false)} onSave={saveEntry} />
      <ViewEntryDialog open={!!viewing} entry={viewing} defaultBlurContent={blurContent} onClose={() => setViewing(null)} />
    </div>
  );
}

export function KluisPage() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [meta, setMeta] = useState<VaultMeta | null>(null);
  // The live vault key lives only in memory (state), never persisted. A ref mirror lets us clear it.
  const vaultKeyRef = useRef<CryptoKey | null>(null);
  const [vaultKey, setVaultKey] = useState<CryptoKey | null>(null);

  useEffect(() => {
    (async () => {
      const res = await apiJson("/vault");
      if (res.ok && res.data.vault) {
        const v = res.data.vault;
        setMeta({
          kdfName: v.kdfName,
          kdfParams: v.kdfParams,
          kdfSalt: v.kdfSalt,
          wrappedKeyIv: v.wrappedKeyIv,
          wrappedKeyCiphertext: v.wrappedKeyCiphertext,
          passwordHint: v.passwordHint ?? null,
        });
        setPhase("locked");
      } else {
        setPhase("setup");
      }
    })();
  }, []);

  const lock = useCallback(() => {
    vaultKeyRef.current = null;
    setVaultKey(null);
    setPhase("locked");
  }, []);

  // Drop the key when leaving the page.
  useEffect(() => () => { vaultKeyRef.current = null; }, []);

  return (
    <AdminShell>
      <AdminPageHeader
        items={[{ label: "Kluis" }]}
        subtitle="Versleutelde notities, alleen leesbaar met jouw Kluis-wachtwoord."
        action={
          phase === "unlocked" ? (
            <Button size="sm" variant="outline" onClick={lock}><LockIcon /> Vergrendelen</Button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><KeyRoundIcon className="size-3.5" /> Client-side versleuteld</span>
          )
        }
      />

      <div className="mt-6">
        {phase === "loading" && <p className="text-sm text-muted-foreground">Laden...</p>}
        {phase === "setup" && (
          <SetupCard
            onCreated={(key, m) => {
              vaultKeyRef.current = key;
              setVaultKey(key);
              setMeta(m);
              setPhase("unlocked");
            }}
          />
        )}
        {phase === "locked" && meta && (
          <UnlockCard
            meta={meta}
            onUnlocked={(key) => {
              vaultKeyRef.current = key;
              setVaultKey(key);
              setPhase("unlocked");
            }}
          />
        )}
        {phase === "unlocked" && vaultKey && <VaultView vaultKey={vaultKey} onLock={lock} />}
      </div>
    </AdminShell>
  );
}
